import React from 'react';

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  label?: string;
};

export function getPaginationItems(current: number, total: number): (number | 'ellipsis-left' | 'ellipsis-right')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  // Near the start: 1 2 3 4 5 ... total
  if (current <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis-right', total];
  }

  // Near the end: 1 ... (total-4) (total-3) (total-2) (total-1) total
  if (current >= total - 3) {
    return [1, 'ellipsis-left', total - 4, total - 3, total - 2, total - 1, total];
  }

  // In the middle: 1 ... (current-1) current (current+1) ... total
  return [1, 'ellipsis-left', current - 1, current, current + 1, 'ellipsis-right', total];
}

export default function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  label = 'mục',
}: PaginationProps) {
  if (totalItems <= pageSize || totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const items = getPaginationItems(currentPage, totalPages);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '20px',
        paddingTop: '16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      <div style={{ color: '#94a3b8', fontSize: '13px' }}>
        Hiển thị <span style={{ color: '#f8fafc', fontWeight: 600 }}>{startItem} - {endItem}</span> trong tổng số{' '}
        <span style={{ color: '#f8fafc', fontWeight: 600 }}>{totalItems}</span> {label}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
        {/* Nút Trước */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          style={{
            padding: '5px 12px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: currentPage <= 1 ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.05)',
            color: currentPage <= 1 ? '#475569' : '#cbd5e1',
            cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.15s ease',
          }}
        >
          ← Trước
        </button>

        {/* Danh sách trang với dấu ba chấm */}
        {items.map((item, idx) => {
          if (item === 'ellipsis-left') {
            return (
              <button
                key={`ellipsis-left-${idx}`}
                type="button"
                onClick={() => onPageChange(Math.max(1, currentPage - 5))}
                title="Lùi 5 trang"
                style={{
                  minWidth: '32px',
                  height: '30px',
                  padding: '0 4px',
                  borderRadius: '8px',
                  border: '1px solid transparent',
                  background: 'transparent',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = '#cbd5e1';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#64748b';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                •••
              </button>
            );
          }

          if (item === 'ellipsis-right') {
            return (
              <button
                key={`ellipsis-right-${idx}`}
                type="button"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 5))}
                title="Tiến 5 trang"
                style={{
                  minWidth: '32px',
                  height: '30px',
                  padding: '0 4px',
                  borderRadius: '8px',
                  border: '1px solid transparent',
                  background: 'transparent',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = '#cbd5e1';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#64748b';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                •••
              </button>
            );
          }

          const pageNum = item as number;
          const isActive = pageNum === currentPage;

          return (
            <button
              key={pageNum}
              type="button"
              onClick={() => onPageChange(pageNum)}
              style={{
                minWidth: '32px',
                height: '30px',
                padding: '0 8px',
                borderRadius: '8px',
                border: isActive ? '1px solid var(--neon-red)' : '1px solid rgba(255, 255, 255, 0.08)',
                background: isActive ? 'rgba(225, 29, 72, 0.25)' : 'rgba(255, 255, 255, 0.03)',
                color: isActive ? '#f8fafc' : '#94a3b8',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: isActive ? 700 : 500,
                boxShadow: isActive ? '0 0 10px rgba(225, 29, 72, 0.35)' : 'none',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.color = '#f8fafc';
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                  e.currentTarget.style.color = '#94a3b8';
                }
              }}
            >
              {pageNum}
            </button>
          );
        })}

        {/* Nút Sau */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          style={{
            padding: '5px 12px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: currentPage >= totalPages ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.05)',
            color: currentPage >= totalPages ? '#475569' : '#cbd5e1',
            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.15s ease',
          }}
        >
          Sau →
        </button>
      </div>
    </div>
  );
}
